import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import emailjs from "@emailjs/browser";
import axios from "axios";
import "./denunciaForm.css";

const DenunciaForm = () => {
  const { t } = useTranslation();

  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    identificacao: "anonima",
    nome: "",
    tipo: "",
    envolvido: "",
    testemunha: "",
    conversou: "",
    detalhes: "",
  });

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [statusMsg, setStatusMsg] = useState("");

  const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  const EMAILJS_SERVICE_ID = process.env.REACT_APP_SERVICE_ID;
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_TEMPLATE_ID_DENUNCIA;
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_PUBLI;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const uploadToCloudinary = async () => {
    if (!file) return { success: true, url: "" };

    setUploading(true);
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", UPLOAD_PRESET);
    data.append("cloud_name", CLOUD_NAME);

    try {
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        data
      );
      setUploadedUrl(res.data.secure_url);
      setUploading(false);
      return { success: true, url: res.data.secure_url };
    } catch (error) {
      setUploading(false);
      return { success: false, url: "" };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMsg(t("denunciaForm.form.status_enviando"));

    const uploadResult = await uploadToCloudinary();

    if (!uploadResult.success) {
      setStatusMsg(t("denunciaForm.form.status_erro_upload"));
      return;
    }

    const templateParams = {
      identificacao: formData.identificacao,
      nome:
        formData.identificacao === "identificada"
          ? formData.nome
          : t("denunciaForm.form.identificacao_opcao_anonima"),
      tipo: formData.tipo,
      envolvido: formData.envolvido,
      testemunha: formData.testemunha,
      conversou: formData.conversou,
      detalhes: formData.detalhes,
      imagem_url: uploadResult.url || t("denunciaForm.form.anexo_sem"),
    };

    emailjs
      .send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        setStatusMsg(t("denunciaForm.form.status_sucesso"));
        setFormData({
          identificacao: "anonima",
          nome: "",
          tipo: "",
          envolvido: "",
          testemunha: "",
          conversou: "",
          detalhes: "",
        });
        setFile(null);
        setUploadedUrl("");
        if (fileInputRef.current) fileInputRef.current.value = "";
      })
      .catch(() => {
        setStatusMsg(t("denunciaForm.form.status_erro_email"));
      });
  };

  return (
    <div className="container py-4">
      <form
        onSubmit={handleSubmit}
        className="p-4 p-md-5 border rounded shadow-sm bg-white"
      >
        <div className="row">
          {/* COLUNA ESQUERDA */}
          <div className="col-lg-5 mb-4 mb-lg-0">
            <div className="p-3 bg-light rounded" style={{ fontSize: "0.9rem" }}>
              <p className="fw-bold text-custom">{t("denunciaForm.guia.titulo")}</p>
              <p className="text-muted text-custom">{t("denunciaForm.guia.descricao")}</p>
              <p className="text-custom">{t("denunciaForm.guia.incluir")}</p>

              <ul className="list-unstyled text-muted text-custom">
                <li><strong>{t("denunciaForm.guia.o_que")}</strong></li>
                <li><strong>{t("denunciaForm.guia.quem")}</strong></li>
                <li><strong>{t("denunciaForm.guia.quando")}</strong></li>
                <li><strong>{t("denunciaForm.guia.onde")}</strong></li>
                <li><strong>{t("denunciaForm.guia.porque")}</strong></li>
                <li><strong>{t("denunciaForm.guia.quanto")}</strong></li>
                <li><strong>{t("denunciaForm.guia.evidencias")}</strong></li>
              </ul>

              <p className="text-muted mt-3 text-custom">{t("denunciaForm.guia.agradecimento")}</p>
            </div>
          </div>

          {/* COLUNA DIREITA */}
          <div className="col-lg-7">
            {/* Identificação */}
            <div className="mb-3">
              <label className="form-label fw-bold">{t("denunciaForm.form.identificacao_label")}</label>
              <select
                name="identificacao"
                className="form-select"
                value={formData.identificacao}
                onChange={handleChange}
              >
                <option value="anonima">{t("denunciaForm.form.identificacao_opcao_anonima")}</option>
                <option value="identificada">{t("denunciaForm.form.identificacao_opcao_identificada")}</option>
              </select>
            </div>

            {/* Nome */}
            {formData.identificacao === "identificada" && (
              <div className="mb-3">
                <label className="form-label fw-bold">{t("denunciaForm.form.nome_label")}</label>
                <input
                  type="text"
                  name="nome"
                  className="form-control"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {/* Tipo */}
            <div className="mb-3">
              <label className="form-label fw-bold">{t("denunciaForm.form.tipo_label")}</label>
              <input
                type="text"
                name="tipo"
                className="form-control"
                value={formData.tipo}
                onChange={handleChange}
                placeholder={t("denunciaForm.form.tipo_placeholder")}
              />
            </div>

            {/* Envolvido */}
            <div className="mb-3">
              <label className="form-label fw-bold">{t("denunciaForm.form.envolvido_label")}</label>
              <textarea
                name="envolvido"
                className="form-control"
                rows="2"
                value={formData.envolvido}
                onChange={handleChange}
              ></textarea>
            </div>

            {/* Testemunha */}
            <div className="mb-3">
              <label className="form-label fw-bold">{t("denunciaForm.form.testemunha_label")}</label>
              <textarea
                name="testemunha"
                className="form-control"
                rows="2"
                value={formData.testemunha}
                onChange={handleChange}
              ></textarea>
            </div>

            {/* Conversou */}
            <div className="mb-3">
              <label className="form-label fw-bold">{t("denunciaForm.form.conversou_label")}</label>
              <select
                name="conversou"
                className="form-select"
                value={formData.conversou}
                onChange={handleChange}
              >
                <option value="">{t("denunciaForm.form.conversou_opcao_selecione")}</option>
                <option value="sim">{t("denunciaForm.form.conversou_opcao_sim")}</option>
                <option value="nao">{t("denunciaForm.form.conversou_opcao_nao")}</option>
              </select>
            </div>

            {/* Detalhes */}
            <div className="mb-3">
              <label className="form-label fw-bold">{t("denunciaForm.form.detalhes_label")}</label>
              <textarea
                name="detalhes"
                className="form-control"
                rows="4"
                value={formData.detalhes}
                onChange={handleChange}
              ></textarea>
            </div>

            {/* Arquivo */}
            <div className="mb-3">
              <label className="form-label fw-bold">{t("denunciaForm.form.anexo_label")}</label>
              <div>
                <button
                  type="button"
                  className="btn btn-secondary w-50"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t("denunciaForm.form.anexo_botao")}
                </button>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </div>

              {/* Mostrar nome do arquivo selecionado */}
              {file && (
                <small className="d-block mt-1 text-primary">
                  {t("denunciaForm.form.anexo_selecionado")}: {file.name}
                </small>
              )}

              {uploading && (
                <small className="text-warning d-block mt-1">
                  {t("denunciaForm.form.anexo_enviando")}
                </small>
              )}

              {uploadedUrl && (
                <small className="text-success d-block mt-1">
                  {t("denunciaForm.form.anexo_sucesso")}
                </small>
              )}
            </div>

            {/* Termo */}
            <div className="form-check mb-3">
              <input className="form-check-input" type="checkbox" required id="termo" />
              <label className="form-check-label" htmlFor="termo">
                {t("denunciaForm.form.termo_label")}
              </label>
            </div>

            {/* Botão */}
            <button type="submit" className="btn btn-success w-50" disabled={uploading}>
              {uploading
                ? t("denunciaForm.form.botao_aguarde")
                : t("denunciaForm.form.botao_enviar")}
            </button>

            {/* Status */}
            {statusMsg && <p className="mt-3 text-center text-success">{statusMsg}</p>}
          </div>
        </div>
      </form>
    </div>
  );
};

export default DenunciaForm;
